import BackgroundImage from "@components/BackgroundImageCst";
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Table from "@components/Table";
import { PagesID } from "@utils/utilsConfigurations";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { ServicesURL } from "@utils/utilsApi";
import ButtonBackPage from "@components/ButtonBackPage";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { useFilters } from "@context/filters";
import { useState } from "react";
import { ActionsType } from "src/interfaces/Common";

const DashboardCorporateAssociation: React.FC = () => {
  const { t, i18n } = useTranslation([
    "dashboard-corporate-association-page",
    "common",
  ]);
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["dashboard.corporate-association"] + "dash"}`;

  const isPO = user?.roles.includes(RolesDigitedEnum.PRODUCT_OWNER);

  const [openFiltersDialog, setOpenFiltersDialog] = useState(false);
  const [selectedDashboardName, setSelectedDashboardName] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);

  const { columns, dataTable, actionsTopToolbar, actionForRow } = useFetchTable(
    `${ServicesURL.getDashboard}?getCorporates=true${isPO ? "" : `&userId=${user?.id_user}`}`,
    PagesID["dashboard.corporate-association"],
    filters[filterId],
  );

  const openFiltersPopup = (row: any) => {
    setSelectedDashboardName(row?.originalName || row?.name || "");
    setSelectedFilters(row?.filters || []);
    setOpenFiltersDialog(true);
  };

  const customRowActions: ActionsType[] = [
    {
      icon: "visibility",
      type: "button",
      permission: "none",
      label: { it: "Vedi filtri", en: "View filters" },
      labelTranslate:
        ({ it: "Vedi filtri", en: "View filters" } as any)[i18n.language] ||
        "View filters",
      callBack: openFiltersPopup,
    },
  ];

  const filterMappingTitle =
    ({ it: "Mappatura filtri", en: "Filter mapping" } as any)[i18n.language] ||
    "Filter mapping";
  const noAssociatedFiltersLabel =
    (
      {
        it: "Nessun filtro associato.",
        en: "No associated filters.",
      } as any
    )[i18n.language] || "No associated filters.";

  const mergedActionForRow = [...(actionForRow || []), ...customRowActions];

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-dashboard.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["dashboard.corporate-association"] + "dash"}
            data={dataTable?.output || []}
            columns={columns || []}
            configurationTopToolbar={actionsTopToolbar}
            configurationActionForRow={mergedActionForRow}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
        <Dialog
          open={openFiltersDialog}
          onClose={() => setOpenFiltersDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {`${filterMappingTitle}${selectedDashboardName ? ` - ${selectedDashboardName}` : ""}`}
          </DialogTitle>
          <DialogContent>
            {!selectedFilters.length ? (
              <Typography>{noAssociatedFiltersLabel}</Typography>
            ) : (
              <MuiTable size="small">
                <TableHead
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& .MuiTableCell-head": {
                      fontWeight: 600,
                      backgroundColor: "#f5f5f5",
                      borderBottom: "2px solid #ddd",
                      color: "#333",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>metabase_param_key</TableCell>
                    <TableCell>ol_user_field</TableCell>
                    <TableCell>operator</TableCell>
                    <TableCell>value_type</TableCell>
                    <TableCell>required</TableCell>
                    <TableCell>default_value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedFilters.map((filter: any, index: number) => (
                    <TableRow
                      key={`${filter?.id || filter?.metabaseParamKey || index}`}
                    >
                      <TableCell>
                        {filter?.metabaseParamKey ||
                          filter?.metabase_param_key ||
                          "-"}
                      </TableCell>
                      <TableCell>
                        {filter?.olUserField || filter?.ol_user_field || "-"}
                      </TableCell>
                      <TableCell>{filter?.operator || "-"}</TableCell>
                      <TableCell>
                        {filter?.valueType || filter?.value_type || "-"}
                      </TableCell>
                      <TableCell>
                        {typeof filter?.required === "boolean"
                          ? String(filter.required)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {filter?.defaultValue || filter?.default_value || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </AnimationFadeIn>
  );
};

export default DashboardCorporateAssociation;
